import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../api/client';
import { useToast } from '../../context/ToastContext';
import BroadcastModal from '../../components/messaging/BroadcastModal';
import { ConfirmModal } from '../../components/ui';
import { Announcement } from '../../types/announcement';
import './AdminAnnouncementsPage.css';
import { translateError } from '../../utils/errorMessages';

const AdminAnnouncementsPage: React.FC = () => {
  const { t } = useTranslation();
  const { addToast } = useToast();
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [broadcastOpen, setBroadcastOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const fetchAnnouncements = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await api.announcements.list();
      setAnnouncements(data as Announcement[]);
    } catch (err: any) {
      setError(translateError(err, t));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAnnouncements();
  }, [fetchAnnouncements]);

  const handleBroadcastClose = (sent?: boolean) => {
    setBroadcastOpen(false);
    if (sent) {
      fetchAnnouncements();
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteId) return;
    setDeleteLoading(true);
    try {
      await api.announcements.delete(deleteId);
      setAnnouncements((prev) => prev.filter((a) => a._id !== deleteId));
      addToast(t('announcements.deleteSuccess'), 'success');
    } catch (err: any) {
      addToast(translateError(err, t, 'announcements.deleteError'), 'error');
    } finally {
      setDeleteLoading(false);
      setDeleteId(null);
    }
  };

  const formatTime = (timestamp: string): string => {
    const date = new Date(timestamp);
    return date.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="admin-announcements-page">
      <div className="admin-announcements-page__header">
        <h2>{t('announcements.title')}</h2>
        <button
          className="admin-announcements-page__new-btn"
          onClick={() => setBroadcastOpen(true)}
        >
          {t('announcements.newBroadcast')}
        </button>
      </div>

      {error && <div className="admin-announcements-page__error">{error}</div>}

      {loading ? (
        <div className="admin-announcements-page__loading">
          <div className="admin-announcements-page__spinner"></div>
        </div>
      ) : announcements.length === 0 ? (
        <div className="admin-announcements-page__empty">
          <p>{t('announcements.noAnnouncements')}</p>
        </div>
      ) : (
        <div className="admin-announcements-page__list">
          {announcements.map((announcement) => (
            <div key={announcement._id} className="admin-announcements-page__card">
              <div className="admin-announcements-page__card-header">
                <div className="admin-announcements-page__sender">
                  <span className="admin-announcements-page__sender-name">
                    {t('announcements.sentBy')} {announcement.sender.name}
                  </span>
                  <span className="admin-announcements-page__time">
                    {formatTime(announcement.createdAt)}
                  </span>
                </div>
                <button
                  className="admin-announcements-page__delete-btn"
                  onClick={() => setDeleteId(announcement._id)}
                  title={t('common.delete')}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" width="16" height="16">
                    <path fillRule="evenodd" d="M8.75 1A2.75 2.75 0 006 3.75v.443c-.795.077-1.584.176-2.365.298a.75.75 0 10.23 1.482l.149-.022.841 10.518A2.75 2.75 0 007.596 19h4.807a2.75 2.75 0 002.742-2.53l.841-10.52.149.023a.75.75 0 00.23-1.482A41.03 41.03 0 0014 4.193V3.75A2.75 2.75 0 0011.25 1h-2.5zM10 4c.84 0 1.673.025 2.5.075V3.75c0-.69-.56-1.25-1.25-1.25h-2.5c-.69 0-1.25.56-1.25 1.25v.325C8.327 4.025 9.16 4 10 4zM8.58 7.72a.75.75 0 00-1.5.06l.3 7.5a.75.75 0 101.5-.06l-.3-7.5zm4.34.06a.75.75 0 10-1.5-.06l-.3 7.5a.75.75 0 101.5.06l.3-7.5z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>

              <p className="admin-announcements-page__message">{announcement.message}</p>

              <div className="admin-announcements-page__targets">
                <span className="admin-announcements-page__targets-label">
                  {t('announcements.targets')}:
                </span>
                {announcement.groups.map((g) => (
                  <span key={g._id} className="admin-announcements-page__tag admin-announcements-page__tag--group">
                    {g.name}
                  </span>
                ))}
                {announcement.events.map((e) => (
                  <span key={e._id} className="admin-announcements-page__tag admin-announcements-page__tag--event">
                    {e.name}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <BroadcastModal isOpen={broadcastOpen} onClose={handleBroadcastClose} />

      <ConfirmModal
        isOpen={!!deleteId}
        onClose={() => setDeleteId(null)}
        onConfirm={handleDeleteConfirm}
        title={t('common.confirmTitle')}
        message={t('announcements.deleteConfirm')}
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        variant="danger"
        loading={deleteLoading}
      />
    </div>
  );
};

export default AdminAnnouncementsPage;
